/**
 * What each part of a report is made of.
 *
 * The guidelines screen used to lay out every control at once: ten PDF
 * colours, six workbook colours, three selects, three toggles and two text
 * fields, in panels named after file formats. To change the rule above a
 * total you had to know that it is called `rule` and that `rule` lives under
 * "PDF guidelines" — the screen was organised by where the value is stored
 * rather than by what a reader is looking at.
 *
 * This inverts that. Each entry below is a REGION OF THE DOCUMENT a person
 * can point at, and the controls that govern it. The specimen paints these
 * regions and makes them clickable; the inspector shows the controls for
 * whichever one is selected. Nobody has to know a field name to restyle
 * something they can see.
 *
 * The mapping is declared once, here, so the specimen and the inspector
 * cannot disagree about which control belongs to which mark.
 */
import type { PdfBrandProfile, XlsxBrandProfile } from '@/services/reportBranding'

export type BrandSurface = 'pdf' | 'xlsx'

export type BrandField =
  | { kind: 'colour'; surface: BrandSurface; name: string; label: string; help?: string; unseen?: boolean }
  | { kind: 'text'; surface: BrandSurface; name: string; label: string; help?: string; unseen?: boolean }
  | { kind: 'toggle'; surface: BrandSurface; name: string; label: string; help?: string; unseen?: boolean }
  | {
      kind: 'select'
      surface: BrandSurface
      name: string
      label: string
      options: [string, string][]
      help?: string
      unseen?: boolean
    }

export type BrandTarget = {
  id: string
  /** What a reader would call this part of the document. */
  label: string
  /** Where it sits, so the inspector can say what is being changed. */
  surface: BrandSurface
  /** One sentence on what the mark does in the finished report. */
  note: string
  fields: BrandField[]
}

const pdfColour = (name: keyof PdfBrandProfile, label: string, help?: string): BrandField => ({
  kind: 'colour', surface: 'pdf', name: String(name), label, help,
})
const xlsxColour = (name: keyof XlsxBrandProfile, label: string, help?: string): BrandField => ({
  kind: 'colour', surface: 'xlsx', name: String(name), label, help,
})

export const BRAND_TARGETS: BrandTarget[] = [
  {
    id: 'cover-eyebrow',
    label: 'Report kind line',
    surface: 'pdf',
    note: 'The small line above the title naming what the document is.',
    fields: [
      pdfColour('accent', 'Accent', 'Rules, section numerals and the mark are set in it too.'),
      {
        ...pdfColour('accentDeep', 'Accent (pressed)', 'Small type set in the accent, where the plain accent would be too light to read.'),
        unseen: true,
      },
    ],
  },
  {
    id: 'cover-title',
    label: 'Cover title',
    surface: 'pdf',
    note: 'The document’s headline — the deal, the portfolio or the sitting.',
    fields: [
      pdfColour('display', 'Display ink', 'Every heading in the report, not only the cover.'),
      {
        kind: 'select', surface: 'pdf', name: 'fontFamily', label: 'Typeface',
        options: [['helvetica', 'Helvetica (sans)'], ['times', 'Times (serif)']],
        help: 'Sets the whole document, headings and body alike.',
      },
    ],
  },
  {
    id: 'metric-strip',
    label: 'Headline metric strip',
    surface: 'pdf',
    note: 'The band under the title carrying the number the report is about.',
    fields: [
      pdfColour('accentPale', 'Strip ground', 'The wash behind the headline metric.'),
      pdfColour('accent', 'Strip rule', 'The line above the strip.'),
    ],
  },
  {
    id: 'table-head',
    label: 'Table head',
    surface: 'pdf',
    note: 'The column headings on every table in the report.',
    fields: [
      {
        kind: 'select', surface: 'pdf', name: 'tableRules', label: 'Treatment',
        options: [['ruled', 'Ruled (house rule)'], ['banded', 'Banded head'], ['boxed', 'Boxed grid']],
        help: 'Ruled draws a line under the head; the others fill it.',
      },
      pdfColour('tableHeadBg', 'Head fill', 'Used when the head is banded or boxed.'),
      pdfColour('tableHeadText', 'Head ink', 'Type on a filled head.'),
    ],
  },
  {
    id: 'table-rows',
    label: 'Table rows',
    surface: 'pdf',
    note: 'The body of every table — the lines between rows and their banding.',
    fields: [
      pdfColour('hairline', 'Row hairline', 'The line between one row and the next.'),
      { kind: 'toggle', surface: 'pdf', name: 'zebra', label: 'Band alternate rows' },
      pdfColour('zebraBg', 'Banding', 'The alternate row ground when banding is on.'),
    ],
  },
  {
    id: 'table-total',
    label: 'Total rule',
    surface: 'pdf',
    note: 'The rule that separates a total from the rows it sums.',
    fields: [pdfColour('rule', 'Section rule', 'Also used above each section heading.')],
  },
  {
    id: 'footer',
    label: 'Footer band',
    surface: 'pdf',
    note: 'The band at the foot of every page, including the confidentiality line.',
    fields: [
      pdfColour('displayDeep', 'Band ground', 'The cover panel is set in the same ink.'),
      {
        kind: 'text', surface: 'pdf', name: 'confidentialityLine', label: 'Confidentiality line',
        help: 'Printed at the foot of every page, before the client name.',
      },
      {
        kind: 'text', surface: 'pdf', name: 'footerCentre', label: 'Footer center',
        help: 'Optional standing text in the middle of the footer.',
      },
    ],
  },
  {
    id: 'page',
    label: 'Page and marks',
    surface: 'pdf',
    note: 'The sheet the report is printed on, and where the logo appears.',
    fields: [
      {
        kind: 'select', surface: 'pdf', name: 'pageSize', label: 'Page size',
        options: [
          ['letter', 'Letter portrait'],
          ['letter-landscape', 'Letter landscape'],
          ['legal-landscape', 'Legal landscape'],
          ['tabloid-landscape', 'Tabloid landscape (11 × 17)'],
          ['a4', 'A4'],
        ],
      },
      { kind: 'toggle', surface: 'pdf', name: 'coverLogoEnabled', label: 'Mark on the cover', unseen: true },
      { kind: 'toggle', surface: 'pdf', name: 'logoEnabled', label: 'Mark in the running head', unseen: true },
    ],
  },
  {
    id: 'xlsx-head',
    label: 'Workbook header row',
    surface: 'xlsx',
    note: 'The first row of every exported sheet.',
    fields: [
      xlsxColour('headerBg', 'Header fill'),
      xlsxColour('headerText', 'Header ink'),
      { kind: 'toggle', surface: 'xlsx', name: 'freezeHeader', label: 'Freeze the header row', unseen: true },
      { kind: 'toggle', surface: 'xlsx', name: 'repeatHeaderRow', label: 'Repeat it on every printed page', unseen: true },
    ],
  },
  {
    id: 'xlsx-rows',
    label: 'Workbook cells',
    surface: 'xlsx',
    note: 'The data rows of an exported sheet.',
    fields: [
      xlsxColour('rowBg', 'Cell ground'),
      xlsxColour('rowText', 'Cell ink'),
      { kind: 'toggle', surface: 'xlsx', name: 'zebra', label: 'Band alternate rows' },
      xlsxColour('rowAltBg', 'Banding'),
      {
        kind: 'select', surface: 'xlsx', name: 'numberStyle', label: 'Number style',
        options: [['accounting', 'Accounting'], ['plain', 'Plain']],
        unseen: true,
      },
    ],
  },
  {
    id: 'xlsx-grid',
    label: 'Workbook gridlines',
    surface: 'xlsx',
    note: 'The borders drawn between cells.',
    fields: [
      { kind: 'toggle', surface: 'xlsx', name: 'gridlines', label: 'Draw gridlines' },
      xlsxColour('gridBorder', 'Grid line'),
      { kind: 'toggle', surface: 'xlsx', name: 'fitToWidth', label: 'Fit to one page wide when printed', unseen: true },
    ],
  },
]

export const TARGETS_BY_ID: Record<string, BrandTarget> = Object.fromEntries(
  BRAND_TARGETS.map((target) => [target.id, target]),
)
